import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { chunk, map } from 'lodash';
import { useMemo } from 'react';

import { Device, GetDevicesPayload, GetDevicesResponse, GetDevicesResult } from '@/api/devices/types';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { queryClient } from '@/common/react-query';
import { useWavelink } from '@/components/providers/WavelinkProvider';
import { DEVICES_LIST_QUERY_KEY, QUERIES_REFETCH_INTERVAL } from '@/config';
import { useViewToken } from '@/context/ViewTokenContext';

export const getDevices = async (
    { devices }: GetDevicesPayload,
    options?: RequestOptions
): Promise<GetDevicesResult> => {
    const response = await axios.post<GetDevicesResponse, GetDevicesResponse>(
        '/fetch/devices',
        {
            devices,
        },
        {
            ...requestConfig(options),
        }
    );

    const results = response.data.currentConditions;
    /**
     * Inject each of the device results into the cache manually so that when we inevitably useDevice() on them, we don't need
     * to fetch them again.
     * We have to do it here vs in useEffect in useDevices because we want to make sure the cache is populated before any of the useDevices calls
     * try to access it, otherwise in the absence of cache it will dispatch multiple single-device fetches
     */
    results.forEach((device) => {
        queryClient.setQueryData(devicesQueryKey([device], !!options?.viewToken), [device]);
    });

    return results;
};

/**
 * How many spotters one request asks about.
 *
 * This bounds the query the endpoint runs, not how many requests are in flight: the axios client caps
 * concurrency for the whole app, and these chunks take their turn in that queue like anything else.
 */
export const DEVICE_CHUNK_LIMIT = 15;

const getDevicesChunked = async (
    { devices }: GetDevicesPayload,
    options?: RequestOptions
): Promise<GetDevicesResult> => {
    if (devices.length <= DEVICE_CHUNK_LIMIT) {
        return getDevices({ devices }, options);
    }

    const resultsChunks = await Promise.all(
        chunk(devices, DEVICE_CHUNK_LIMIT).map((devicesChunk) => getDevices({ devices: devicesChunk }, options))
    );

    // in the order the chunks were cut, so the result follows the order the spotters were asked for
    return resultsChunks.flat();
};

/**
 * `TSelected` is what the caller reads, which is the whole device list unless `config.select`
 * narrows it. Narrowing is worth reaching for here: wavelink invalidates `devices-list` on nearly
 * every message, and react-query compares the selected value, so a consumer that only needs one
 * field re-renders only when that field changes.
 */
export type UseDevicesOptions<TSelected = GetDevicesResult> = {
    config?: Omit<UseQueryOptions<GetDevicesResult, Error, TSelected>, 'queryKey' | 'queryFn'>;
} & GetDevicesPayload;

/**
 * Writes one device into every cached devices-list query in place.
 *
 * A devices-list query can hold the user's whole fleet, so invalidating one to pick up a single spotter
 * refetches every other spotter with it.
 *
 * The `map` allocates a new array, which react-query then puts through structural sharing: anything that
 * compares deep-equal keeps its previous reference, so unrelated rows stay identical and a list with no
 * changed row keeps the array itself and notifies nobody. Only the patched row becomes a new object.
 *
 * Only rows already in a list are updated, so this cannot add or remove a spotter.
 */
const patchCachedDevice = (spotterId: string, device: Device): void => {
    queryClient.setQueriesData<Device[]>({ queryKey: [DEVICES_LIST_QUERY_KEY] }, (oldList) => {
        if (!oldList) {
            return oldList;
        }

        return oldList.map((cached) => (cached.spotterId === spotterId ? device : cached));
    });
};

/**
 * Apply a wavelink "devices-list" invalidation for one spotter by fetching that spotter alone and
 * patching it in, so a reported message never costs a refetch of the rest of the fleet.
 *
 * Without a spotterId there is nothing to narrow against, so the whole list goes.
 */
export const applyDevicesListInvalidation = async (spotterId?: string): Promise<void> => {
    if (!spotterId) {
        await queryClient.invalidateQueries({ queryKey: [DEVICES_LIST_QUERY_KEY] });
        return;
    }

    await refreshCachedDevice(spotterId);
};

/**
 * Refetch one device and write it into every cached devices-list query.
 *
 * A failure is reported rather than thrown: every caller fires this without awaiting, so the row keeps
 * the value it already had until something refetches it, which beats an unhandled rejection.
 */
export const refreshCachedDevice = async (spotterId: string): Promise<void> => {
    try {
        const [device] = await getDevices({ devices: [{ spotterId }] });
        if (device) {
            patchCachedDevice(spotterId, device);
        }
    } catch (error) {
        console.error(`failed to refresh cached device ${spotterId}`, error);
    }
};

const devicesQueryKey = (devices: { spotterId: string }[], hasViewToken: boolean) => [
    DEVICES_LIST_QUERY_KEY,
    map(devices, 'spotterId'),
    { isPublic: hasViewToken },
];

export const useDevices = <TSelected = GetDevicesResult>({
    config: { enabled = true, ...config } = {},
    devices,
}: UseDevicesOptions<TSelected>) => {
    const { loading: loadingToken, viewToken } = useViewToken();
    const { isQueryCovered } = useWavelink();

    const cleanDevices = useMemo(() => devices?.map(({ spotterId }) => ({ spotterId })), [devices]);
    const queryKey = useMemo(() => devicesQueryKey(cleanDevices, !!viewToken), [cleanDevices, viewToken]);
    const isCoveredByWavelink = isQueryCovered(queryKey);

    const result = useQuery<GetDevicesResult, Error, TSelected>({
        ...config,
        enabled: enabled && !!cleanDevices?.length && !loadingToken,
        queryKey,
        queryFn: ({ signal }) => getDevicesChunked({ devices: cleanDevices }, { viewToken, signal }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchInterval: isCoveredByWavelink ? false : QUERIES_REFETCH_INTERVAL,
        placeholderData: () => {
            // Use any spotters we already have as placeholders, so the map/list at least shows the one you just navigated from.
            const requestedSpotterIds = new Set(cleanDevices.map((device) => device.spotterId));
            const allCachedDevicesRaw = queryClient.getQueriesData<Device[]>({ queryKey: [DEVICES_LIST_QUERY_KEY] });
            const matchedCachedDevices = allCachedDevicesRaw.flatMap(
                ([, cachedQueryData]) =>
                    cachedQueryData?.filter((device) => requestedSpotterIds.has(device.spotterId)) || []
            );
            if (!matchedCachedDevices.length) return undefined;
            return matchedCachedDevices;
        },
    });

    return result;
};
